import React, { FC, useEffect, useState, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Stripe, loadStripe } from '@stripe/stripe-js'

import routerSelectors from '@kaidam/fc-shared/src/frontend/store/selectors/router'
import routerActions from '@kaidam/fc-shared/src/frontend/store/modules/router'
import snackbarActions from '@kaidam/fc-shared/src/frontend/store/modules/snackbar'

import {
  handlers,
} from '@kaidam/fc-shared/src/frontend/store/utils/api'

import {
  IData_Manage_Login_Request,
  IData_Manage_Login_Response,
  IData_Manage_Update_Request,
  IData_Calendar_Public_Day,
  IData_Calendar_Public_Day_Merged,
  IData_Booking_Payment_Request,
  IData_Manage_Booking_Payment_Request,
  IData_Booking_Payment_Response,
  IData_Manage_Booking_Confirm_Request,
  IData_Booking_Confirm_Response,
  IData_Booking_General_Config,
  IData_Booking_Settings_Org,
} from '@kaidam/fc-shared/src/types/schema/booking'

import {
  getDaysFromToday,
} from '@kaidam/fc-shared/src/booking/utils'

import {
  DEFAULT_GENERAL_CONFIG,
} from './defaultValues'

import {
  mergePublicCalendar,
} from '@kaidam/fc-shared/src/booking/model'

export type IManagePageName = 'booking' | 'payments' | 'details' | 'presurvey' | 'postsurvey' | 'monthfeedback' | 'yearfeedback'

const PAGE_SCORES: Record<IManagePageName, number> = {
  booking: 0,
  payments: 1,
  details: 2,
  presurvey: 3,
  postsurvey: 4,
  monthfeedback: 5,
  yearfeedback: 6,
}

const PAGES: IManagePageName[] = [
  'booking',
  'payments',
  'details',
  'presurvey',
  'postsurvey',
  'monthfeedback',
  'yearfeedback',
]

export interface IViewingCalendar {
  date: string,
  length: number,
}

export interface IManageContext {
  loading: boolean,
  token: string,
  initialised: boolean,
  loginError: string,
  pageIndex: number,
  alerts: {
    title: string,
    message: string,
  }[],
  activeForms: Record<string, boolean>,
  calendar: IData_Calendar_Public_Day_Merged[],
  generalConfig: IData_Booking_General_Config,
  login: {
    (payload: IData_Manage_Login_Request): void,
  },
  openPage: {
    (index: number): void,
  },
  update: {
    (payload: IData_Manage_Update_Request, formCompleted?: string): void,
  },
  setViewingCalendar: {
    (val: IViewingCalendar | null): void,
  },
  pay: {
    (amount: number): void,
  },
  cancelBooking: {
    (): Promise<void>,
  }
  checkPayment: {
    (): void,
  },
  viewingCalendar?: IViewingCalendar,
  data?: IData_Manage_Login_Response,
}

export const defaultManageContext: IManageContext = {
  loading: false,
  initialised: false,
  loginError: '',
  token: '',
  pageIndex: 0,
  calendar: [],
  alerts: [],
  activeForms: {},
  generalConfig: DEFAULT_GENERAL_CONFIG,
  openPage: () => {},
  pay: () => {},
  cancelBooking: async () => {},
  checkPayment: () => {},
  data: undefined,
  login: (payload) => {},
  update: (payload, formCompleted) => {},
  setViewingCalendar: () => {},
  viewingCalendar: undefined,

}

const useManage = ({

} = {}): IManageContext => {

  const dispatch = useDispatch()
  const params = useSelector(routerSelectors.params)
  const route = useSelector(routerSelectors.route)

  const [initialised, setInitialised] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [generalConfig, setGeneralConfig] = useState<IData_Booking_General_Config>(undefined)
  const [viewingCalendar, setViewingCalendar] = useState<IViewingCalendar>(null)
  const [calendar, setCalendar] = useState<IData_Calendar_Public_Day_Merged[]>([])
  const [token, setToken] = useState('')
  const [data, setData] = useState<IData_Manage_Login_Response>()

  const alerts = useMemo(() => {
    if(!data || !data.booking) return []
    const {
      submitted_at_presurvey,
      submitted_at_postsurvey,
      submitted_at_monthfeedback,
      submitted_at_yearfeedback,
      data: {
        date,
      }
    } = data.booking

    const bookingDaysFromNow = getDaysFromToday(date)

    if(bookingDaysFromNow > 0 && !submitted_at_presurvey) {
      return [{
        title: 'Pre-survey',
        message: 'Please complete the pre-survey form before your booking date',
      }]
    }

    if(bookingDaysFromNow < 0 && !submitted_at_postsurvey) {
      return [{
        title: 'Post-survey',
        message: 'Please complete the post-survey form',
      }]
    }

    if(bookingDaysFromNow < -30 && !submitted_at_monthfeedback) {
      return [{
        title: 'One month feedback',
        message: 'Please complete the one month feedback form',
      }]
    }

    if(bookingDaysFromNow < -365 && !submitted_at_yearfeedback) {
      return [{
        title: 'Year feedback',
        message: 'Please complete the year feedback form',
      }]
    }

    return []
  }, [
    data,
  ])

  const activeForms = useMemo(() => {
    if(!data || !data.booking) return {}
    const {
      submitted_at_presurvey,
      submitted_at_postsurvey,
      submitted_at_monthfeedback,
      submitted_at_yearfeedback,
      data: {
        date,
      }
    } = data.booking

    const bookingDaysFromNow = getDaysFromToday(date)
    const activeForms: Record<string, boolean> = {}

    if(bookingDaysFromNow > 0 && !submitted_at_presurvey) {
      activeForms.presurvey = true
    }

    if(bookingDaysFromNow < 0 && !submitted_at_postsurvey) {
      activeForms.postsurvey = true
    }

    if(bookingDaysFromNow < -30 && !submitted_at_monthfeedback) {
      activeForms.onemonth = true
    }

    if(bookingDaysFromNow < -365 && !submitted_at_yearfeedback) {
      activeForms.oneyear = true
    }

    return activeForms
  }, [
    data,
  ])

  const pageIndex = useMemo(() => {
    const pageName = route.name.split('.').pop() as IManagePageName
    return PAGE_SCORES[pageName]
  }, [
    route,
  ])

  const openPage = useCallback((index: number) => {
    dispatch(routerActions.navigateTo(['manage', PAGES[index]].join('.'), {
      orgid: data.org.id,
    }))
  }, [
    data,
  ])

  const login = useCallback(async (payload: IData_Manage_Login_Request) => {
    setLoading(true)
    try {
      const data = await handlers.post<IData_Manage_Login_Request, IData_Manage_Login_Response>(`/ft_booking_public/manage_login`, payload)
      setData(data)
      setLoginError('')
      localStorage.setItem('manage_token', data.token)
      document.location.reload()
      // setToken(data.token)

      // setTimeout(() => {
      //   dispatch(routerActions.navigateTo('manage.booking', {
      //     orgid: data.org.id,
      //   }))
      // }, 1000)
    } catch(e) {
      const errorString = e.response ? e.response.data.error : e.toString()
      dispatch(snackbarActions.setError(errorString))
      setLoginError(errorString)
      localStorage.removeItem('manage_token')
      console.log(errorString)
    }
    setLoading(false)
  }, [])

  const update = useCallback(async (payload: IData_Manage_Update_Request, formCompleted = '') => {
    setLoading(true)
    try {
      await handlers.post<IData_Manage_Update_Request, any>(`/ft_booking_public/manage_update/${data.org.id}/${data.booking.id}`, payload, {
        params: {
          access_token: token,
          formCompleted,
        }
      })
      const result = await handlers.get<IData_Manage_Login_Response>(`/ft_booking_public/manage_status`, {
        params: {
          access_token: token,
        }
      })
      setData(result)
      dispatch(snackbarActions.setSuccess(`booking updated`))
    } catch(e) {
      const errorString = e.response ? e.response.data.error : e.toString()
      dispatch(snackbarActions.setError(errorString))
      console.log(errorString)
    }
    setLoading(false)
  }, [
    data,
    token,
  ])

  const cancelBooking = useCallback(async () => {
    setLoading(true)
    try {
      await handlers.post<IData_Manage_Update_Request, any>(`/ft_booking_public/manage_cancel/${data.org.id}/${data.booking.id}`, {}, {
        params: {
          access_token: token,
        }
      })
      const result = await handlers.get<IData_Manage_Login_Response>(`/ft_booking_public/manage_status`, {
        params: {
          access_token: token,
        }
      })
      setData(result)
      dispatch(snackbarActions.setSuccess(`booking cancelled`))
    } catch(e) {
      const errorString = e.response ? e.response.data.error : e.toString()
      dispatch(snackbarActions.setError(errorString))
      console.log(errorString)
    }
    setLoading(false)
  }, [
    data,
    token,
  ])

  const pay = useCallback(async (amount: number) => {
    setLoading(true)
    try {
      if(!data.booking) throw new Error(`no booking`)
      const payload: IData_Manage_Booking_Payment_Request = {
        org: data.booking.org_id,
        id: data.booking.id,
        amount,
        cancel_url: document.location.href,
        success_url: document.location.href + '?confirm_payment=yes',
      }
      const paymentData = await handlers.post<IData_Manage_Booking_Payment_Request, IData_Booking_Payment_Response>(`/ft_booking_public/manage_booking_payment`, payload, {
        params: {
          access_token: token,
        }
      })
      localStorage.setItem('manage_booking_payment_id', paymentData.session.id)
      const stripe = await loadStripe(paymentData.public_key)
      if (!stripe) throw new Error(`stripe client could not be created`)
      const { error } = await stripe.redirectToCheckout({
        sessionId: paymentData.session.id,
      })
      if (error) throw error
    } catch (e) {
      const errorString = e.response ? e.response.data.error : e.toString()
      dispatch(snackbarActions.setError(errorString))
      setLoading(false)
    }
  }, [
    data,
    token,
  ])

  const checkPayment = useCallback(async () => {
    
    const paymentId = localStorage.getItem('manage_booking_payment_id')
    if(!paymentId || !params.confirm_payment) return

    setLoading(true)

    try {
      const result = await handlers.post<IData_Manage_Booking_Confirm_Request, IData_Booking_Confirm_Response>(`/ft_booking_public/manage_booking_confirm`, {
        org: data.org.id,
        id: data.booking.id,
        session_id: paymentId,
      }, {
        params: {
          access_token: token,
        }
      })
      if(!result || !result.confirmed) throw new Error(`payment not confirmed`)
      const newData = await handlers.get<IData_Manage_Login_Response>(`/ft_booking_public/manage_status`, {
        params: {
          access_token: token,
        }
      })
      setData(newData)
      dispatch(snackbarActions.setSuccess(`your payment has been confirmed`))
      localStorage.removeItem('manage_booking_payment_id')
    } catch(e) {
      const errorString = e.response ? e.response.data.error : e.toString()
      dispatch(snackbarActions.setError(errorString))
    }

    setLoading(false)
  }, [
    data,
    token,
    params,
  ])

  // load the public calendar
  useEffect(() => {
    if (!data || !data.org || !data.org.id) return
    if(!data.booking || !data.booking.data || !data.booking.data.product) return
    if(!viewingCalendar) return
    const doAsync = async () => {
      const calendarData = await handlers.get<IData_Calendar_Public_Day[]>(`/ft_booking_public/calendar/${data.org.id}`, {
        params: {
          date: viewingCalendar.date,
          days: 3,
          length: viewingCalendar.length,
        },
      })
      setCalendar(mergePublicCalendar(calendarData))
    }
    doAsync()
  }, [
    viewingCalendar,
  ])

  useEffect(() => {
    const doAsync = async () => {
      setLoading(true)
      const storageToken = localStorage.getItem('manage_token')
      if(!storageToken) {
        if(route.name != 'login') {
          dispatch(routerActions.navigateTo('login'))
        }
      }
      else {
        try {
          const data = await handlers.get<IData_Manage_Login_Response>(`/ft_booking_public/manage_status`, {
            params: {
              access_token: storageToken,
            }
          })
          setData(data)
          setToken(storageToken)
          setLoginError('')
          if(route.name == 'login') {
            dispatch(routerActions.navigateTo('manage.booking', {
              orgid: data.org.id,
            }))
          }
        } catch(e) {
          const errorString = e.response ? e.response.data.error : e.toString()
          dispatch(snackbarActions.setError(errorString))
          setLoginError(errorString)
          localStorage.removeItem('manage_token')
          console.log(errorString)
          dispatch(routerActions.navigateTo('login'))
        }
      }

      setLoading(false)
      setInitialised(true)
    }
    doAsync()
  }, [])

  useEffect(() => {
    if(!initialised) return
    if(route.name != 'login' && !data) {
      dispatch(routerActions.navigateTo('login'))
    }
  }, [
    route,
  ])

  useEffect(() => {
    if (!data || !data.org || !data.org.id) return
    const doAsync = async () => {
      const settingsData = await handlers.get<IData_Booking_Settings_Org>(`/ft_booking_public/org_settings/${data.org.id}`)
      setGeneralConfig(settingsData.settings.general_config || DEFAULT_GENERAL_CONFIG)
    }
    doAsync()
  }, [
    data,
  ])


  return {
    loading,
    initialised,
    pageIndex,
    token,
    openPage,
    pay,
    checkPayment,
    calendar,
    alerts,
    activeForms,
    generalConfig,
    loginError,
    data,
    update,
    login,
    cancelBooking,
    setViewingCalendar,
    viewingCalendar,
  }
}

export default useManage
